import React, { useEffect, useState } from "react";
import { Link, Routes, Route, NavLink, useNavigate, useParams } from "react-router-dom";
import { api, formatCurrency } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, LineChart, Line } from "recharts";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { ArrowUpRight, Plus, Pencil, Trash2, BadgeCheck, Sparkles, Megaphone, Receipt, TrendingUp, Download, Inbox as InboxIcon, Mail, MailOpen, Archive, Send } from "lucide-react";
import { API } from "../lib/api";
import ListBusiness from "./ListBusiness";

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (user === false) nav("/login", { state: { from: "/dashboard" } }); }, [user, nav]);
  if (!user) return null;

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10" data-testid="dashboard-page">
      <Seo title="Dashboard — BizzAtlas" description="Manage your BizzAtlas listings, analytics, ads, inbox and billing." />
      <div className="grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="space-y-1">
          <div className="text-xs font-mono-tag uppercase tracking-[0.2em] text-zinc-400 mb-3">/ Account</div>
          <SideLink to="/dashboard" end label="Overview" testid="dash-link-overview" />
          <SideLink to="/dashboard/inbox" label="Inbox" testid="dash-link-inbox" badge="inbox" />
          <SideLink to="/dashboard/listings" label="My listings" testid="dash-link-listings" />
          <SideLink to="/dashboard/new" label="New listing" testid="dash-link-new" />
          <SideLink to="/dashboard/ads" label="Ad campaigns" testid="dash-link-ads" />
          <SideLink to="/dashboard/invoices" label="Invoices" testid="dash-link-invoices" />
          <div className="mt-6 rounded-lg border border-zinc-200 p-4 bg-zinc-50/60">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-400 mb-1">Plan</div>
            <div className="font-display text-lg capitalize">{user.membership}</div>
            <div className="text-xs text-zinc-500 mb-3">{user.ad_credits} ad credits</div>
            <Button asChild size="sm" className="w-full btn-brand" data-testid="dash-upgrade-btn"><Link to="/pricing">Upgrade →</Link></Button>
          </div>
        </aside>
        <div>
          <Routes>
            <Route index element={<Overview />} />
            <Route path="listings" element={<MyListings />} />
            <Route path="new" element={<ListBusiness />} />
            <Route path="edit/:id" element={<EditListing />} />
            <Route path="ads" element={<AdsPanel onPurchased={refresh} />} />
            <Route path="inbox" element={<InboxPanel />} />
            <Route path="invoices" element={<InvoicesPanel />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

function SideLink({ to, end, label, testid, badge }) {
  const [unread, setUnread] = React.useState(0);
  React.useEffect(() => {
    if (badge !== "inbox") return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/me/messages", { params: { unread: true } });
        if (!cancelled) setUnread(data.unread_count || 0);
      } catch (_) { /* silent — keep previous count */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [badge]);

  return (
    <NavLink to={to} end={end} data-testid={testid}
      className={({ isActive }) => `flex items-center justify-between px-3 py-2 rounded-md text-sm transition ${isActive ? "surface-navy" : "text-zinc-600 hover:bg-zinc-100"}`}>
      <span>{label}</span>
      {badge === "inbox" && unread > 0 && (
        <span className="text-[10px] font-mono-tag bg-red-500 text-white rounded-full px-1.5 min-w-[18px] text-center" data-testid="inbox-unread-badge">{unread}</span>
      )}
    </NavLink>
  );
}

function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/analytics").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-zinc-400 font-mono-tag">Loading…</div>;
  const t = data.totals;

  return (
    <div data-testid="dash-overview">
      <h1 className="font-display text-4xl tracking-tight mb-2">Overview</h1>
      <p className="text-sm text-zinc-500 mb-8">A quick pulse of your presence on BizzAtlas.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        <Stat label="Listings" value={t.businesses} />
        <Stat label="Total views" value={t.views} />
        <Stat label="Total clicks" value={t.clicks} accent />
        <Stat label="Ad credits" value={t.ad_credits} />
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="font-display flex items-center justify-between">
            14-day activity
            <Badge variant="outline" className="font-mono-tag uppercase text-[10px]">Views & clicks</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div style={{ width: "100%", minHeight: 260, height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="views" stroke="#0F172A" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="clicks" stroke="#0055FF" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">Per-listing performance</CardTitle></CardHeader>
        <CardContent>
          {data.businesses.length === 0 ? (
            <div className="text-sm text-zinc-500">No listings yet. <Link className="underline" to="/dashboard/new">Create one →</Link></div>
          ) : (
            <div style={{ width: "100%", minHeight: 280, height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.businesses.map(b => ({ name: b.name.slice(0, 14), views: b.views, clicks: b.clicks }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="views" fill="#2563EB" />
                  <Bar dataKey="clicks" fill="#0055FF" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-lg border ${accent ? "border-zinc-900 surface-navy" : "border-zinc-200 bg-white"} p-5`}>
      <div className={`text-xs uppercase tracking-[0.18em] ${accent ? "text-zinc-400" : "text-zinc-500"} font-mono-tag`}>{label}</div>
      <div className="font-display text-3xl mt-2">{value}</div>
    </div>
  );
}

function MyListings() {
  const [items, setItems] = useState([]);
  const [sponsorOpen, setSponsorOpen] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/me/businesses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/businesses/${id}`);
    toast.success("Listing deleted");
    load();
  };

  return (
    <div data-testid="dash-listings">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-4xl tracking-tight">My listings</h1>
        <Button onClick={() => nav("/dashboard/new")} className="btn-brand" data-testid="dash-create-listing-btn">
          <Plus className="h-4 w-4 mr-1" />Create listing
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed rounded-xl p-16 text-center">
          <div className="text-sm font-mono-tag uppercase text-zinc-400 tracking-widest">No listings yet</div>
          <div className="font-display text-2xl mt-2">Get found by B2B buyers — in minutes.</div>
          <Button onClick={() => nav("/dashboard/new")} className="mt-6 btn-brand" data-testid="dash-empty-create">Create your first listing</Button>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((b) => (
            <div key={b.id} className="rounded-lg border border-zinc-200 bg-white p-5 flex items-center justify-between" data-testid={`my-biz-${b.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Link to={`/business/${b.id}`} className="font-display text-xl hover:underline">{b.name}</Link>
                  {b.verified && <BadgeCheck className="h-4 w-4 text-blue-600" />}
                  {b.sponsored_until && new Date(b.sponsored_until) > new Date() && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">Sponsored</Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{b.views} views · {b.clicks} clicks · {b.engagement_seconds}s engagement</div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => setSponsorOpen(b)} data-testid={`sponsor-btn-${b.id}`}><Sparkles className="h-4 w-4 mr-1" />Sponsor</Button>
                <Button size="sm" variant="outline" onClick={() => nav(`/dashboard/edit/${b.id}`)} data-testid={`edit-btn-${b.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" onClick={() => del(b.id)} data-testid={`del-btn-${b.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <SponsorDialog open={!!sponsorOpen} business={sponsorOpen} onClose={() => { setSponsorOpen(null); load(); }} />
    </div>
  );
}

function SponsorDialog({ open, business, onClose }) {
  const { user, refresh } = useAuth();
  const [days, setDays] = useState(7);
  const credits = days * 50;
  if (!business) return null;
  const ok = (user?.ad_credits || 0) >= credits;
  const purchase = async () => {
    try {
      await api.post("/purchase", { type: "sponsor", business_id: business.id, days });
      toast.success(`'${business.name}' is now sponsored for ${days} day(s).`);
      await refresh();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sponsor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Sponsor {business.name}</DialogTitle>
          <DialogDescription>Promote this listing to the top of search and category pages.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Label>Duration (days)</Label>
          <Input type="number" min={1} max={60} value={days} onChange={(e) => setDays(Math.max(1, parseInt(e.target.value || 1)))} data-testid="sponsor-days-input" />
          <div className="text-sm text-zinc-600">Cost: <b>{credits}</b> ad credits</div>
          <div className="text-xs text-zinc-500">You have {user?.ad_credits || 0} credits. {ok ? "" : "Top up below."}</div>
        </div>
        <DialogFooter className="gap-2">
          {!ok && <Button asChild variant="outline" onClick={onClose}><Link to="/dashboard/ads">Buy credits</Link></Button>}
          <Button onClick={purchase} disabled={!ok} className="btn-brand" data-testid="sponsor-confirm-btn">Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditListing() {
  const { id } = useParams();
  const [biz, setBiz] = useState(null);
  const nav = useNavigate();
  useEffect(() => { if (id) api.get(`/businesses/${id}`).then((r) => setBiz(r.data)); }, [id]);
  if (!biz) return <div className="text-zinc-400 font-mono-tag">Loading…</div>;
  return <ListBusiness editing={biz} onSaved={() => nav("/dashboard/listings")} />;
}

function AdsPanel({ onPurchased }) {
  const { user, refresh } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [coupon, setCoupon] = useState("");
  const load = () => Promise.all([
    api.get("/me/ads").then((r) => setCampaigns(r.data)),
    api.get("/me/notifications").then((r) => setNotifs(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const buyCredits = async (amount) => {
    if (!user?.email_verified) {
      toast.error("Please verify your email before purchasing.");
      return;
    }
    try {
      const { data } = await api.post("/checkout", { type: "ad_credits", credits: amount, origin_url: window.location.origin });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Checkout could not be started.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Purchase failed"); }
  };

  const packs = [
    { credits: 100, price: "$19" },
    { credits: 500, price: "$79" },
    { credits: 2000, price: "$249" },
  ];

  return (
    <div data-testid="dash-ads">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl tracking-tight">Ad campaigns</h1>
          <p className="text-sm text-zinc-500 mt-1">Top up credits to sponsor listings and surface in featured rails.</p>
        </div>
        <div className="rounded-lg border border-zinc-200 px-4 py-2 text-sm">
          <span className="text-zinc-500">Balance:</span> <b className="font-mono-tag">{user?.ad_credits || 0} credits</b>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-6">
        {packs.map((p) => (
          <div key={p.credits} className="rounded-xl border border-zinc-200 bg-white p-6 card-lift" data-testid={`credits-pack-${p.credits}`}>
            <Megaphone className="h-5 w-5 text-zinc-900" />
            <div className="font-display text-3xl mt-4">{p.credits} <span className="text-base text-zinc-500">credits</span></div>
            <div className="text-sm text-zinc-500 mb-5">{p.price} — one-time</div>
            <Button onClick={() => buyCredits(p.credits)} className="w-full btn-brand" data-testid={`buy-credits-${p.credits}`}>Buy</Button>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-zinc-200 bg-zinc-50/60 p-4 mb-10 flex items-center gap-3 max-w-md">
        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 font-mono-tag shrink-0">Coupon</div>
        <input
          value={coupon}
          onChange={(e) => setCoupon(e.target.value.toUpperCase())}
          placeholder="OPTIONAL CODE"
          className="flex-1 bg-transparent text-sm font-mono-tag focus:outline-none uppercase placeholder:text-zinc-400"
          data-testid="ads-coupon-input"
        />
        {coupon && <button onClick={() => setCoupon("")} className="text-xs text-zinc-400 hover:text-zinc-900">clear</button>}
      </div>

      <h2 className="font-display text-2xl mb-3">Your campaigns</h2>
      {campaigns.length === 0 ? (
        <div className="border border-dashed rounded-xl p-10 text-center text-zinc-500 text-sm">No campaigns yet. Sponsor a listing from <Link to="/dashboard/listings" className="underline">My listings</Link>.</div>
      ) : (
        <div className="space-y-2">
          {campaigns.map((c) => (
            <div key={c.id} className="rounded-lg border border-zinc-200 bg-white p-4 flex items-center justify-between" data-testid={`campaign-${c.id}`}>
              <div>
                <div className="font-medium">{c.business_name}</div>
                <div className="text-xs text-zinc-500 mt-0.5 font-mono-tag">{c.credits_remaining}/{c.credits_total} remaining · {c.clicks} clicks</div>
              </div>
              <Link to={`/business/${c.business_id}`} className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1">view <ArrowUpRight className="h-3 w-3" /></Link>
            </div>
          ))}
        </div>
      )}

      {notifs.length > 0 && (
        <>
          <h2 className="font-display text-2xl mb-3 mt-10">Notifications</h2>
          <div className="space-y-2">
            {notifs.map((n) => (
              <div key={n.id} className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2" data-testid={`notif-${n.id}`}>
                <TrendingUp className="h-4 w-4 mt-0.5" /><div>{n.message}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InvoicesPanel() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/invoices").then((r) => setItems(r.data)); }, []);

  const downloadPdf = async (iid) => {
    const tok = localStorage.getItem("bizzatlas_token");
    const res = await fetch(`${API}/invoices/${iid}/pdf`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : {},
      credentials: "include",
    });
    if (!res.ok) { toast.error("Could not download PDF"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bizzatlas-invoice-${iid.slice(0, 8).toUpperCase()}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="dash-invoices">
      <h1 className="font-display text-4xl tracking-tight mb-6">Invoices</h1>
      {items.length === 0 ? (
        <div className="border border-dashed rounded-xl p-10 text-center text-zinc-500 text-sm">No invoices yet.</div>
      ) : (
        <div className="rounded-xl border border-zinc-200 overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-xs">
              <tr><th className="text-left p-4">Date</th><th className="text-left p-4">Item</th><th className="text-left p-4">Status</th><th className="text-right p-4">Amount</th><th className="text-right p-4">PDF</th></tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="border-t border-zinc-100" data-testid={`invoice-row-${i.id}`}>
                  <td className="p-4 font-mono-tag text-zinc-500">{i.created_at?.slice(0,10)}</td>
                  <td className="p-4">
                    <div className="font-medium">{i.item}</div>
                    <div className="text-xs text-zinc-500">{i.description}</div>
                  </td>
                  <td className="p-4"><Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-emerald-50"><Receipt className="h-3 w-3 mr-1" />{i.status}</Badge></td>
                  <td className="p-4 text-right font-mono-tag">{formatCurrency(i.amount_cents, i.currency)}</td>
                  <td className="p-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadPdf(i.id)} data-testid={`invoice-pdf-${i.id}`}>
                      <Download className="h-4 w-4 mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function InboxPanel() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState("all"); // all | unread
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/me/messages", { params: filter === "unread" ? { unread: true } : {} });
    setItems(data.items);
    setUnread(data.unread_count);
  };
  useEffect(() => { load(); }, [filter]);

  const open = async (m) => {
    setActive(m);
    if (!m.read) {
      try { await api.post(`/me/messages/${m.id}/read`); } catch (_) { /* ignore — UI proceeds */ }
      load();
    }
  };
  const archive = async (mid) => { await api.post(`/me/messages/${mid}/archive`); toast.success("Archived"); setActive(null); load(); };
  const remove = async (mid) => { if (!window.confirm("Delete this message?")) return; await api.delete(`/me/messages/${mid}`); toast.success("Deleted"); setActive(null); load(); };

  return (
    <div data-testid="dash-inbox">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">Inbox {unread > 0 && <Badge className="bg-red-500 hover:bg-red-500 text-white">{unread} unread</Badge>}</h1>
          <p className="text-sm text-zinc-500 mt-1">Messages from buyers across all your listings.</p>
        </div>
        <div className="flex items-center gap-1 text-sm">
          <Button size="sm" variant={filter === "all" ? "default" : "outline"} onClick={() => setFilter("all")} className={filter === "all" ? "btn-brand" : ""} data-testid="inbox-filter-all">All</Button>
          <Button size="sm" variant={filter === "unread" ? "default" : "outline"} onClick={() => setFilter("unread")} className={filter === "unread" ? "btn-brand" : ""} data-testid="inbox-filter-unread">Unread</Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed rounded-xl p-16 text-center">
          <InboxIcon className="h-8 w-8 text-zinc-400 mx-auto" />
          <div className="text-sm font-mono-tag uppercase text-zinc-400 tracking-widest mt-4">No messages</div>
          <div className="font-display text-2xl mt-2">You&apos;re all caught up.</div>
          <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">Buyers can message you from any of your listing pages. Sponsored listings tend to receive 3-5× more.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-[360px_1fr] gap-4">
          <div className="space-y-2 max-h-[700px] overflow-y-auto scroll-soft pr-1">
            {items.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => open(m)}
                data-testid={`inbox-msg-${m.id}`}
                className={`w-full text-left rounded-lg border p-4 transition ${active?.id === m.id ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {!m.read && <span className="h-2 w-2 rounded-full bg-blue-600 shrink-0" />}
                    <div className="font-medium truncate">{m.sender_name}</div>
                  </div>
                  <div className="text-[10px] font-mono-tag text-zinc-400 whitespace-nowrap">{m.created_at?.slice(0, 10)}</div>
                </div>
                <div className="text-sm text-zinc-700 truncate mt-0.5">{m.subject}</div>
                <div className="text-xs text-zinc-500 truncate mt-1">to {m.business_name}</div>
              </button>
            ))}
          </div>
          <div className="rounded-xl border border-zinc-200 bg-white p-6">
            {!active ? (
              <div className="h-full grid place-items-center text-sm text-zinc-400">Select a message</div>
            ) : (
              <div data-testid="inbox-detail">
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div>
                    <div className="font-display text-2xl">{active.subject}</div>
                    <div className="text-sm text-zinc-600 mt-1">
                      From <b>{active.sender_name}</b> &lt;<a href={`mailto:${active.sender_email}`} className="text-blue-700 hover:underline">{active.sender_email}</a>&gt;
                      {active.sender_phone && <> · {active.sender_phone}</>}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono-tag">
                      <Link to={`/business/${active.business_id}`} className="hover:text-zinc-900">{active.business_name}</Link> · {active.created_at?.replace("T", " ").slice(0, 16)}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => archive(active.id)} data-testid={`inbox-archive-${active.id}`}><Archive className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(active.id)} data-testid={`inbox-del-${active.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </div>
                </div>
                <div className="text-sm whitespace-pre-line text-zinc-800 leading-relaxed" data-testid="inbox-msg-body">{active.body}</div>
                {Array.isArray(active.replies) && active.replies.length > 0 && (
                  <div className="mt-6 pt-5 border-t border-zinc-100 space-y-4" data-testid="inbox-replies">
                    <div className="text-xs font-mono-tag uppercase tracking-[0.18em] text-zinc-400">Your replies</div>
                    {active.replies.map((r) => (
                      <div key={r.id} className="rounded-md bg-zinc-50 border border-zinc-100 p-4">
                        <div className="text-xs text-zinc-500 mb-1 font-mono-tag">{r.by_name} · {r.created_at?.replace("T", " ").slice(0, 16)}</div>
                        <div className="text-sm whitespace-pre-line text-zinc-800">{r.body}</div>
                      </div>
                    ))}
                  </div>
                )}
                <ReplyComposer
                  message={active}
                  onSent={async (updated) => {
                    setActive(updated);
                    await load();
                  }}
                />
                <div className="mt-6 flex items-center gap-2 pt-6 border-t border-zinc-100">
                  <Button asChild variant="outline" data-testid="inbox-mailto-btn">
                    <a href={`mailto:${active.sender_email}?subject=Re: ${encodeURIComponent(active.subject)}`}>
                      <Mail className="h-4 w-4 mr-1.5" />Email outside BizzAtlas
                    </a>
                  </Button>
                  {active.first_reply_at && (
                    <span className="text-xs text-emerald-700 flex items-center gap-1 font-mono-tag">
                      replied {Math.max(1, Math.round(((active.response_seconds || 0) / 60)))} min after first message
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function ReplyComposer({ message, onSent }) {
  const [body, setBody] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const send = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/me/messages/${message.id}/reply`, { body });
      toast.success("Reply sent");
      setBody("");
      onSent && onSent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reply failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 rounded-lg border border-zinc-200 bg-white p-4" data-testid="reply-composer">
      <div className="text-xs font-mono-tag uppercase tracking-[0.18em] text-zinc-400 mb-2">Reply directly from BizzAtlas</div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder={`Hi ${message.sender_name?.split(" ")[0] || "there"}, thanks for reaching out…`}
        rows={4}
        maxLength={8000}
        className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900/10 focus:border-zinc-900"
        data-testid="reply-composer-body"
      />
      <div className="mt-3 flex items-center justify-between">
        <div className="text-[10px] text-zinc-400 font-mono-tag">{body.length}/8000 · sent via your business email</div>
        <Button size="sm" onClick={send} disabled={busy || !body.trim()} className="btn-brand" data-testid="reply-composer-send">
          <Send className="h-4 w-4 mr-1.5" />{busy ? "Sending…" : "Send reply"}
        </Button>
      </div>
    </div>
  );
}
